import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import '@babylonjs/core/Rendering/edgesRenderer';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import '@babylonjs/core/Layers/effectLayerSceneComponent';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';

import { PALETTE } from '@observant/shared';
import type { CrossingState } from '../sim/crossing.js';
import { DEFAULT_CROSSING_CONFIG as ROOM } from '../sim/crossing.js';
import { createQubitMesh, type QubitMesh } from './qubitMesh.js';
import { createBackground, type Background } from './background.js';

function edge(mesh: Mesh, hex: string, width = 2): void {
  const c = Color3.FromHexString(hex);
  mesh.enableEdgesRendering();
  mesh.edgesWidth = width;
  mesh.edgesColor = new Color4(c.r, c.g, c.b, 1);
}

const UP = new Vector3(0, 1, 0);
function orientSegment(cyl: Mesh, p0: Vector3, p1: Vector3): void {
  const dir = p1.subtract(p0);
  const len = dir.length();
  cyl.scaling.y = len;
  cyl.position.copyFrom(p0.add(p1).scale(0.5));
  const nd = dir.scale(1 / Math.max(1e-4, len));
  const dot = Math.max(-1, Math.min(1, Vector3.Dot(UP, nd)));
  const axis = Vector3.Cross(UP, nd);
  if (axis.lengthSquared() < 1e-6) {
    cyl.rotationQuaternion = Quaternion.Identity();
  } else {
    cyl.rotationQuaternion = Quaternion.RotationAxis(axis.normalize(), Math.acos(dot));
  }
}

export class CrossingScene {
  readonly engine: Engine;
  readonly scene: Scene;
  private camera: UniversalCamera;
  private background: Background;
  private qubit: QubitMesh;
  private segs: Mesh[] = [];
  private joints: Mesh[] = [];
  private bob: Mesh;
  private bobMat: StandardMaterial;
  private blochSphere: Mesh;
  private peekX = 0;
  private peekY = 0;

  constructor(engine: Engine) {
    this.engine = engine;
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.01, 0.015, 0.03, 1);

    this.camera = new UniversalCamera('cam', new Vector3(0, 4.4, 1), this.scene);
    this.camera.fov = 0.95;
    this.camera.minZ = 0.1;
    this.camera.maxZ = 400;

    const glow = new GlowLayer('glow', this.scene, { blurKernelSize: 24 });
    glow.intensity = 0.85;

    const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), this.scene);
    hemi.intensity = 0.4;
    const key = new PointLight('key', new Vector3(0, ROOM.mount.y + 1, ROOM.mount.z), this.scene);
    key.intensity = 0.7;

    this.background = createBackground(this.scene);

    const dark = new StandardMaterial('dark', this.scene);
    dark.diffuseColor = new Color3(0.05, 0.06, 0.09);
    dark.emissiveColor = new Color3(0.015, 0.02, 0.03);

    const w = ROOM.xMax - ROOM.xMin;
    const d = ROOM.zMax - ROOM.zMin;
    const cz = (ROOM.zMin + ROOM.zMax) / 2;

    const road = CreateBox('road', { width: w, height: 0.3, depth: d }, this.scene);
    road.position.set(0, -0.15, cz);
    road.material = dark;
    edge(road, PALETTE.neonCyan, 1);

    // Danger band markings under the swing.
    for (const z of [ROOM.mount.z - ROOM.swingRadius, ROOM.mount.z + ROOM.swingRadius]) {
      const line = CreateBox('band', { width: w, height: 0.06, depth: 0.12 }, this.scene);
      line.position.set(0, 0.02, z);
      line.material = (() => {
        const m = new StandardMaterial(`band-${z}`, this.scene);
        m.emissiveColor = new Color3(1, 0.5, 0.1);
        m.disableLighting = true;
        return m;
      })();
    }

    // Gantry over the street.
    for (const sx of [ROOM.xMin, ROOM.xMax]) {
      const post = CreateBox(
        `post-${sx}`,
        { width: 0.35, height: ROOM.mount.y + 1, depth: 0.35 },
        this.scene,
      );
      post.position.set(sx, (ROOM.mount.y + 1) / 2, ROOM.mount.z);
      post.material = dark;
      edge(post, PALETTE.neonCyan, 1);
    }
    const beam = CreateBox('beam', { width: w, height: 0.35, depth: 0.35 }, this.scene);
    beam.position.set(0, ROOM.mount.y + 0.8, ROOM.mount.z);
    beam.material = dark;
    edge(beam, PALETTE.neonCyan, 1);

    // Faint Bloch sphere at the mount — the "state space" the bob precesses on.
    this.blochSphere = CreateSphere(
      'bloch',
      { diameter: ROOM.swingRadius * 2, segments: 12 },
      this.scene,
    );
    this.blochSphere.position.set(ROOM.mount.x, ROOM.mount.y, ROOM.mount.z);
    const blochMat = new StandardMaterial('blochMat', this.scene);
    blochMat.emissiveColor = Color3.FromHexString(PALETTE.neonCyan).scale(0.5);
    blochMat.wireframe = true;
    blochMat.disableLighting = true;
    blochMat.alpha = 0.25;
    this.blochSphere.material = blochMat;
    this.blochSphere.isPickable = false;

    // The 3-segment chain + joints + heavy bob.
    const chainMat = new StandardMaterial('chain', this.scene);
    chainMat.diffuseColor = new Color3(0.6, 0.62, 0.7);
    chainMat.emissiveColor = new Color3(0.05, 0.06, 0.08);
    for (let i = 0; i < 3; i++) {
      const seg = CreateCylinder(`seg-${i}`, { diameter: 0.14, height: 1 }, this.scene);
      seg.material = chainMat;
      this.segs.push(seg);
    }
    for (let i = 0; i < 2; i++) {
      const j = CreateSphere(`joint-${i}`, { diameter: 0.3, segments: 10 }, this.scene);
      j.material = chainMat;
      this.joints.push(j);
    }
    this.bobMat = new StandardMaterial('bobMat', this.scene);
    this.bobMat.diffuseColor = new Color3(0.5, 0.52, 0.6);
    this.bobMat.specularColor = new Color3(1, 1, 1);
    this.bobMat.emissiveColor = new Color3(0.4, 0.2, 0.05);
    this.bob = CreateSphere('bob', { diameter: 1.6, segments: 24 }, this.scene);
    this.bob.material = this.bobMat;

    // EXIT door.
    const frame = CreateBox('exit', { width: 2.4, height: 3.4, depth: 0.3 }, this.scene);
    frame.position.set(ROOM.mount.x, 1.7, ROOM.zMax - 0.2);
    const exitMat = new StandardMaterial('exitMat', this.scene);
    exitMat.emissiveColor = new Color3(0.1, 1, 0.45);
    exitMat.disableLighting = true;
    frame.material = exitMat;
    frame.visibility = 0.5;
    edge(frame, '#19f7a0', 3);

    this.qubit = createQubitMesh(this.scene);

    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  }

  private onResize(): void {
    this.engine.resize();
  }

  setLook(x: number, y: number): void {
    this.peekX = x;
    this.peekY = y;
  }

  sync(state: CrossingState): void {
    this.qubit.root.position.set(state.pos.x, 0.9, state.pos.z);
    this.qubit.root.rotation.y = state.facing;
    this.qubit.tick(state.t);

    // Pendulum chain (mount → 2 wobbling joints → bob) for the triple look.
    const mount = new Vector3(ROOM.mount.x, ROOM.mount.y, ROOM.mount.z);
    const bobPos = new Vector3(state.bob.x, state.bob.y, state.bob.z);
    const flat = new Vector3(bobPos.x - mount.x, 0, bobPos.z - mount.z);
    const perp = new Vector3(-flat.z, 0, flat.x);
    if (perp.lengthSquared() > 1e-4) perp.normalize();
    const w1 = Math.sin(state.t * 1.7) * 0.7;
    const w2 = Math.sin(state.t * 1.1 + 1) * 0.5;
    const j1 = Vector3.Lerp(mount, bobPos, 1 / 3).add(perp.scale(w1));
    const j2 = Vector3.Lerp(mount, bobPos, 2 / 3).add(perp.scale(w2));
    orientSegment(this.segs[0]!, mount, j1);
    orientSegment(this.segs[1]!, j1, j2);
    orientSegment(this.segs[2]!, j2, bobPos);
    this.joints[0]!.position.copyFrom(j1);
    this.joints[1]!.position.copyFrom(j2);
    this.bob.position.copyFrom(bobPos);

    // Bob glows red as it nears your lane (danger), cool when clear.
    const danger = Math.max(0, 1 - state.bobDist / (ROOM.hitRadius * 2.2));
    this.bobMat.emissiveColor = new Color3(0.3 + danger * 0.9, 0.18 - danger * 0.1, 0.05);

    // The Bloch sphere precesses with the swing.
    this.blochSphere.rotation.y = state.precession;

    // Chase camera, clamped inside the street.
    const cam = this.camera.position;
    const tx = state.pos.x * 0.4 + this.peekX * 3;
    const tz = Math.max(ROOM.zMin + 0.6, state.pos.z - 4.5);
    cam.x += (tx - cam.x) * 0.12;
    cam.z += (tz - cam.z) * 0.12;
    cam.y = 4.4;
    this.camera.setTarget(
      new Vector3(state.pos.x * 0.25 + this.peekX * 5, 1.6 + this.peekY * 2, state.pos.z + 6),
    );

    this.background.mesh.position.set(cam.x, 6, state.pos.z + 60);
    const aspect = this.engine.getRenderWidth() / Math.max(1, this.engine.getRenderHeight());
    this.background.update(state.t, aspect);
  }

  render(): void {
    this.scene.render();
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    this.scene.dispose();
  }
}
